import { createAction } from '@reduxjs/toolkit';

import type {
	ChatMessage,
	ChatMessagesPage,
	ConversationSummary,
	PrivateChatUnlockedPacket,
} from '../../services/chatApi';

/** The conversations list, newest activity first. */
export const chatConversationsRequest = createAction('chat/conversations/request');
export const chatConversationsSuccess = createAction<{ items: ConversationSummary[] }>(
	'chat/conversations/success',
);
export const chatConversationsFailure = createAction<string>('chat/conversations/failure');

/** The total unread across conversations, for the header dot and the Chats button. */
export const chatUnreadCountRequest = createAction('chat/unreadCount/request');
export const chatUnreadCountSuccess = createAction<{ count: number }>('chat/unreadCount/success');

/** Which conversation (if any) the player shares with `playerId`, and whether it is unlocked. */
export const chatResolveRequest = createAction<{ playerId: string }>('chat/resolve/request');
export const chatResolveSuccess = createAction<{
	playerId: string;
	conversationId: string | null;
	unlocked: boolean;
}>('chat/resolve/success');
export const chatResolveFailure = createAction<{ playerId: string; message: string }>(
	'chat/resolve/failure',
);

/** The newest page of a thread, or with `older` the page before the oldest loaded message. */
export const chatMessagesRequest = createAction<{ conversationId: string; older?: boolean }>(
	'chat/messages/request',
);
export const chatMessagesSuccess = createAction<
	ChatMessagesPage & { conversationId: string; older: boolean }
>('chat/messages/success');
export const chatMessagesFailure = createAction<{
	conversationId: string;
	older: boolean;
	message: string;
}>('chat/messages/failure');

/**
 * Sends `text`, shown at once as a pending bubble keyed by `clientId`. Dispatching it again with
 * the same `clientId` retries a failed send in place.
 */
export const chatSendRequest = createAction<{
	conversationId: string;
	clientId: string;
	text: string;
	senderId: string;
	recipientId: string;
}>('chat/send/request');
export const chatSendSuccess = createAction<{
	conversationId: string;
	clientId: string;
	message: ChatMessage;
}>('chat/send/success');
export const chatSendFailure = createAction<{
	conversationId: string;
	clientId: string;
	message: string;
	/** False when sending again cannot help: the chat refused the sender (a block either way). */
	retryable: boolean;
}>('chat/send/failure');

export const chatMarkReadRequest = createAction<{ conversationId: string }>(
	'chat/markRead/request',
);

/** The open thread: its messages arrive already read, so they do not count as unread. */
export const chatOpened = createAction<{ conversationId: string }>('chat/opened');
export const chatClosed = createAction<{ conversationId: string }>('chat/closed');

/** Socket `privateMessage`. `myId` tells the player's own messages (other sessions) apart. */
export const chatMessageReceived = createAction<{ message: ChatMessage; myId: string }>(
	'chat/message/received',
);
/** Socket `privateChatUnlocked`. */
export const chatUnlocked = createAction<PrivateChatUnlockedPacket>('chat/unlocked');
